/**
 * 중개 fetch 요청 정화 — 결정 #17(권한 등급제 + 도메인 범위 중개 fetch).
 *
 * 스크립트는 raw fetch를 절대 받지 못한다. 대신 호스트의 브로커를 거치는데, 브로커는
 * (a) 도메인 allowlist 강제, (b) ambient 자격증명 제거, (c) SSRF 방어를 한다.
 * 여기서는 그 "정화 규칙"을 순수 함수로 둬 단위 테스트로 고정한다(실제 네트워크
 * 호출은 web의 FetchBroker 구현이 수행).
 *
 * 핵심(결정 #17 / risu-migration §4):
 *  - allowlist + SSRF 방어는 isUrlAllowed로 위임(평문/사설/메타데이터/자격증명 URL 차단).
 *  - 요청 헤더에서 자격증명·쿠키·앱 키 누출 경로를 제거(스크립트가 사용자 키를
 *    외부로 빼돌리지 못하게).
 *  - 메서드 화이트리스트(GET/POST/PUT/PATCH/DELETE)만 허용.
 */
import { isUrlAllowed } from "./policy.js";

/** 정화된 중개 요청(브로커가 그대로 fetch에 넘길 수 있는 형태). */
export interface SanitizedRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
}

/** 자격증명/민감 헤더 차단 목록(소문자, 결정 #17). */
const BLOCKED_HEADERS = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "proxy-authorization",
  "x-api-key", // 프로바이더 키 누출 방지
  "api-key",
  "x-goog-api-key",
  "openai-organization",
  "anthropic-version", // 스크립트가 프로바이더 사칭하지 못하게
]);

const ALLOWED_METHODS = new Set(["GET", "POST", "PUT", "PATCH", "DELETE"]);

export interface BrokerRequestInput {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

/**
 * 스크립트의 fetch 요청을 정화한다(결정 #17). 허용되지 않으면 throw — 브로커는
 * 이 함수가 던지면 요청을 거부해야 한다(절대 우회 금지).
 *
 * @param allowlist 스크립트의 granted network.domains.
 */
export function sanitizeBrokeredRequest(
  input: string,
  init: BrokerRequestInput | undefined,
  allowlist: string[],
): SanitizedRequest {
  // 1) URL 게이트: allowlist + https + SSRF 방어(자격증명 URL 포함 차단).
  if (!isUrlAllowed(input, allowlist)) {
    throw new Error(`중개 fetch 거부: 허용되지 않은 URL (${input})`);
  }

  // 2) 메서드 화이트리스트.
  const method = (init?.method ?? "GET").toUpperCase();
  if (!ALLOWED_METHODS.has(method)) {
    throw new Error(`중개 fetch 거부: 허용되지 않은 메서드 (${method})`);
  }

  // 3) 헤더 정화: 자격증명/민감 헤더 제거. 나머지는 통과.
  const headers: Record<string, string> = {};
  for (const [rawKey, value] of Object.entries(init?.headers ?? {})) {
    const key = rawKey.toLowerCase();
    if (BLOCKED_HEADERS.has(key)) continue;
    // Set-Cookie류 응답 헤더나 hop-by-hop은 요청에 무의미 — 그냥 스킵.
    if (key.startsWith("proxy-") || key.startsWith("sec-")) continue;
    headers[rawKey] = value;
  }

  const out: SanitizedRequest = { url: input, method, headers };
  if (method !== "GET" && method !== "HEAD" && init?.body != null) {
    out.body = init.body;
  }
  return out;
}
